import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { manageSubscriptionTarget, shouldUseAppleIAP, getDisplayPriceUSD, isIOS } from '../utils/platform';
import * as iap from '../utils/iapBridge';
import { 
  User, 
  Mail, 
  Lock, 
  Shield, 
  CreditCard, 
  Trash2, 
  Save, 
  Calendar,
  Key
} from 'lucide-react';
import { api } from '../utils/api';
import { format } from 'date-fns';
import AmbientCaptureSettings from '../components/AmbientCaptureSettings';
import EmailPreferencesCard from '../components/EmailPreferencesCard';
import InstallAppCard from '../components/InstallAppCard';
import PrivacyAICard from '../components/PrivacyAICard';

const Settings = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);

  // Profile state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [profileChanged, setProfileChanged] = useState(false);

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Unified subscribe action that dispatches to the platform-appropriate flow:
  //   - iOS:  Apple StoreKit 2 IAP via native bridge (App Review 3.1.1 compliant)
  //   - Other: Stripe Checkout in a redirect (unchanged web behaviour)
  const startSubscribe = async () => {
    if (typeof window !== 'undefined' && typeof window.__iapDebug === 'function') {
      window.__iapDebug(
        `Settings.startSubscribe fired · shouldUseAppleIAP=${shouldUseAppleIAP()}`
      );
    }
    if (shouldUseAppleIAP()) {
      if (!iap.isNativeIAPAvailable()) {
        toast.error('App Store purchase unavailable', {
          description: 'Please update to the latest version of the app.',
        });
        return;
      }
      const productId =
        process.env.REACT_APP_APPLE_PRODUCT_ID ||
        'com.selahdownloads.subscription.monthly';
      const result = await iap.purchase(productId);
      if (!result.ok) {
        if (result.cancelled) return; // user dismissed the Apple sheet
        toast.error('Purchase could not complete', {
          description: result.error || 'Please try again in a moment.',
        });
        return;
      }
      try {
        const verify = await api.verifyAppleTransaction({
          signed_transaction: result.signedTransaction,
          signed_renewal_info: result.signedRenewalInfo || null,
        });
        if (verify?.is_active) {
          toast.success('Welcome to Selah — subscription active.');
          setTimeout(() => window.location.reload(), 900);
        } else {
          toast.error('Purchase completed but activation failed', {
            description: 'Please contact support@selahdownloads.com.',
          });
        }
      } catch (e) {
        toast.error('Activation failed', { description: e?.message });
      }
      return;
    }

    // Non-iOS: Stripe checkout redirect (unchanged).
    try {
      const { url } = await api.createStripeCheckoutSession(window.location.origin);
      if (url) window.location.href = url;
    } catch (e) {
      toast.error('Could not start checkout', { description: e?.message });
    }
  };

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      const changed = name !== user.name || email !== user.email;
      setProfileChanged(changed);
    }
  }, [name, email, user]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updates = {};
      if (name !== user.name) updates.name = name;
      if (email !== user.email) updates.email = email;

      if (Object.keys(updates).length === 0) {
        toast.info('No changes to save');
        return;
      }

      await api.updateMe(updates);
      toast.success('Profile updated successfully!');
      setProfileChanged(false);
      
      // Refresh user data
      window.location.reload();
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await api.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });

      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await api.deleteMe();
      toast.success('Account deleted successfully');
      logout();
      navigate('/');
    } catch (error) {
      toast.error(error.message || 'Failed to delete account');
    }
  };

  const getDaysRemaining = () => {
    if (!user?.trial?.trial_end) return 0;
    const endDate = new Date(user.trial.trial_end);
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  return (
    <div className="min-h-screen bg-[var(--sd-bg)] py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-semibold text-[var(--sd-fg)] mb-2">Settings</h1>
          <p className="text-[var(--sd-muted-fg)]">Manage your account and preferences</p>
        </motion.div>

        <div className="space-y-6">
          {/* Profile Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="glass-card p-6" data-testid="settings-profile-card">
              <div className="flex items-center gap-3 mb-6">
                <User size={24} className="text-[var(--sd-primary)]" />
                <h2 className="text-xl font-semibold text-[var(--sd-fg)]">Profile Information</h2>
              </div>

              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--sd-fg)] mb-2">
                    Name
                  </label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full text-[#1C1C1C] font-semibold placeholder:text-[#666666]"
                    data-testid="settings-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--sd-fg)] mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full text-[#1C1C1C] font-semibold placeholder:text-[#666666]"
                    data-testid="settings-email-input"
                  />
                </div>

                <div className="flex items-center gap-2 text-sm text-[var(--sd-muted-fg)]">
                  <Calendar size={16} />
                  <span>Member since {user?.created_at ? format(new Date(user.created_at), 'MMMM d, yyyy') : 'N/A'}</span>
                </div>

                {profileChanged && (
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-[var(--sd-primary)] hover:bg-[#D7A1A8] text-[var(--sd-primary-fg)]"
                    data-testid="settings-save-profile-button"
                  >
                    <Save size={16} className="mr-2" />
                    Save Changes
                  </Button>
                )}
              </form>
            </Card>
          </motion.div>

          {/* Security Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="glass-card p-6" data-testid="settings-security-card">
              <div className="flex items-center gap-3 mb-6">
                <Lock size={24} className="text-[var(--sd-primary)]" />
                <h2 className="text-xl font-semibold text-[var(--sd-fg)]">Security</h2>
              </div>

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--sd-fg)] mb-2">
                    Current Password
                  </label>
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full text-[#1C1C1C] font-semibold placeholder:text-[#666666]"
                    data-testid="settings-current-password-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--sd-fg)] mb-2">
                    New Password
                  </label>
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full text-[#1C1C1C] font-semibold placeholder:text-[#666666]"
                    data-testid="settings-new-password-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--sd-fg)] mb-2">
                    Confirm New Password
                  </label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full text-[#1C1C1C] font-semibold placeholder:text-[#666666]"
                    data-testid="settings-confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                  variant="outline"
                  className="border-[var(--sd-primary)] text-[var(--sd-primary)] hover:bg-[var(--sd-primary)]/10"
                  data-testid="settings-change-password-button"
                >
                  <Shield size={16} className="mr-2" />
                  Change Password
                </Button>
              </form>

              <Separator className="my-6 bg-[rgba(245,245,245,0.10)]" />

              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Key size={18} className="text-[var(--sd-primary)]" />
                  <h3 className="text-sm font-semibold text-[var(--sd-fg)]">Encryption</h3>
                </div>
                <p className="text-sm text-[var(--sd-muted-fg)] leading-relaxed">
                  Your downloads are encrypted using your email address. All data is encrypted client-side before being stored.
                </p>
              </div>
            </Card>
          </motion.div>

          {/* Ambient Capture Settings — Zero-Friction Voice Capture */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 }}
          >
            <AmbientCaptureSettings />
          </motion.div>

          {/* Email Preferences — Weekly Reflection Digest */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.285 }}
          >
            <EmailPreferencesCard />
          </motion.div>

          {/* Install as PWA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.29 }}
          >
            <InstallAppCard />
          </motion.div>

          {/* Subscription Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="glass-card p-6" data-testid="settings-subscription-card">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard size={24} className="text-[var(--sd-primary)]" />
                <h2 className="text-xl font-semibold text-[var(--sd-fg)]">Subscription</h2>
              </div>

              {user?.trial?.is_trial ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-[var(--sd-primary)]/20 text-[var(--sd-primary)] border-[var(--sd-primary)]/40">
                      Free Trial
                    </Badge>
                    <span className="text-sm text-[var(--sd-muted-fg)]">
                      {getDaysRemaining()} days remaining
                    </span>
                  </div>
                  {/* Post-IAP era: on iOS the subscribe CTA routes through
                      Apple StoreKit 2 (App Review 3.1.1 compliant). On web /
                      Android / Windows it continues to route through Stripe.
                      startSubscribe() dispatches to the correct provider. */}
                  <p className="text-sm text-[var(--sd-muted-fg)] leading-relaxed">
                    Your trial automatically cancels after 7 days unless you subscribe. No credit card required.
                  </p>
                  <Button
                    onClick={startSubscribe}
                    className="bg-[var(--sd-primary)] hover:brightness-110 text-[var(--sd-primary-fg)] font-semibold"
                    data-testid="settings-upgrade-button"
                  >
                    <CreditCard size={16} className="mr-2" />
                    Subscribe — {`$${getDisplayPriceUSD().toFixed(2)} / mo`}
                  </Button>
                </div>
              ) : user?.subscription?.is_active ? (
                (() => {
                  // ── Reconcile "Active + expired" contradiction ─────────
                  // If the backend flag `is_active` is still true but the
                  // renewal date has already passed (e.g. a legacy Stripe
                  // subscription whose webhook never fired to flip the
                  // flag), treat the subscription as expired for display
                  // purposes. This prevents users from seeing a green
                  // "Active" badge alongside "expired" text — a real bug
                  // reported on Sept 11 2026.
                  const expiryIso =
                    user?.subscription?.apple_expires_at ||
                    user?.subscription?.expires_at ||
                    user?.subscription?.current_period_end;
                  let expiryPassed = false;
                  if (expiryIso) {
                    const d = new Date(expiryIso);
                    if (!Number.isNaN(d.getTime()) && d.getTime() < Date.now()) {
                      expiryPassed = true;
                    }
                  }
                  const provider = user?.subscription?.payment_provider;
                  const isFounderComp = provider === 'manual_comp';
                  // Founder / reviewer complimentary accounts stay "Active"
                  // even past their far-future expiry marker (they're not
                  // billing-driven). For every other provider, respect the
                  // real expiry date.
                  const treatAsActive = isFounderComp || !expiryPassed;
                  if (!treatAsActive) {
                    return (
                      <div className="space-y-4" data-testid="settings-subscription-expired-card">
                        <Badge className="bg-[var(--sd-muted-bg)] text-[var(--sd-muted-fg)] border-[var(--sd-border)]">
                          Subscription ended
                        </Badge>
                        <p className="text-sm text-[var(--sd-muted-fg)] leading-relaxed">
                          Your previous subscription has ended. Resubscribe to unlock ambient capture, deeper analysis, follow-up timelines, and every future feature.
                        </p>
                        <Button
                          onClick={startSubscribe}
                          className="bg-[var(--sd-primary)] hover:brightness-110 text-[var(--sd-primary-fg)] font-semibold"
                          data-testid="settings-resubscribe-button"
                        >
                          <CreditCard size={16} className="mr-2" />
                          Subscribe — {`$${getDisplayPriceUSD().toFixed(2)} / mo`}
                        </Button>
                      </div>
                    );
                  }
                  return (
                <div className="space-y-5" data-testid="settings-active-subscription-card">
                  {/* Row 1: Plan + Provider + Price */}
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <Badge className="bg-[#4FB286]/20 text-[#4FB286] border-[#4FB286]/40">
                      Active &middot; {isFounderComp ? 'Founder' : (user?.subscription?.plan || 'Monthly')}
                    </Badge>
                    {/* Provider badge tells the user (and the reviewer)
                        which billing rails handle their subscription so
                        cancel / refund guidance below is unambiguous. */}
                    {(() => {
                      const p = user?.subscription?.payment_provider;
                      if (p === 'apple_iap') {
                        return (
                          <Badge
                            className="bg-[rgba(212,175,55,0.14)] text-[#D4AF37] border-[rgba(212,175,55,0.35)]"
                            data-testid="settings-subscription-provider-badge"
                          >
                            Apple
                          </Badge>
                        );
                      }
                      if (p === 'manual_comp') {
                        return (
                          <Badge
                            className="bg-[rgba(183,110,121,0.14)] text-[#B76E79] border-[rgba(183,110,121,0.4)]"
                            data-testid="settings-subscription-provider-badge"
                          >
                            Complimentary
                          </Badge>
                        );
                      }
                      if (p === 'stripe' || p === 'google_play' || p === 'microsoft_store') {
                        const label = p === 'stripe' ? 'Web' : p === 'google_play' ? 'Google Play' : 'Microsoft Store';
                        return (
                          <Badge
                            className="bg-[rgba(178,190,181,0.14)] text-[var(--sd-muted-fg)] border-[var(--sd-border)]"
                            data-testid="settings-subscription-provider-badge"
                          >
                            {label}
                          </Badge>
                        );
                      }
                      return null;
                    })()}
                    {/* Price display — HIDDEN on iOS for non-Apple providers
                        to avoid Guideline 3.1.1 anti-steering flags (external
                        pricing shown inside the iOS build). Also hidden for
                        complimentary accounts since price is $0. */}
                    {(() => {
                      const p = user?.subscription?.payment_provider;
                      const price = user?.subscription?.price_usd;
                      if (typeof price !== 'number' || price <= 0) return null;
                      if (p === 'manual_comp') return null;
                      // iOS anti-steering: hide external (non-Apple) pricing.
                      if (isIOS() && p !== 'apple_iap') return null;
                      return (
                        <span
                          className="text-sm text-[var(--sd-muted-fg)]"
                          data-testid="settings-subscription-price"
                        >
                          ${Number(price).toFixed(2)}/mo
                        </span>
                      );
                    })()}
                  </div>

                  {/* Row 2: Renewal — absolute date + relative ("in 12 days") */}
                  {(() => {
                    // Prefer Apple's authoritative expiry when present.
                    const isoDate =
                      user?.subscription?.apple_expires_at ||
                      user?.subscription?.expires_at ||
                      user?.subscription?.current_period_end;
                    if (!isoDate) return null;
                    let dateStr = '';
                    let relative = '';
                    try {
                      const d = new Date(isoDate);
                      if (!Number.isNaN(d.getTime())) {
                        dateStr = d.toLocaleDateString(undefined, {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        });
                        const days = Math.round((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                        if (days === 0) relative = 'today';
                        else if (days === 1) relative = 'tomorrow';
                        else if (days > 1 && days <= 30) relative = `in ${days} days`;
                        else if (days < 0) relative = 'expired';
                      }
                    } catch (_e) { /* ignore */ }
                    const cancelled = !!user?.subscription?.cancelled;
                    return (
                      <div
                        className="rounded-lg px-3.5 py-3 border border-[var(--sd-border)] bg-[rgba(245,245,245,0.03)]"
                        data-testid="settings-subscription-renewal"
                      >
                        <p className="text-[11px] uppercase tracking-[0.14em] text-[var(--sd-muted-fg)] mb-1">
                          {cancelled ? 'Access ends' : 'Renews'}
                        </p>
                        <p className="text-[15px] font-medium text-[var(--sd-fg)]">
                          {dateStr}
                          {relative && (
                            <span className="text-[13px] font-normal text-[var(--sd-muted-fg)] ml-2">
                              &middot; {relative}
                            </span>
                          )}
                        </p>
                      </div>
                    );
                  })()}

                  {/* Row 3: Cancel guidance — provider-specific */}
                  <p
                    className="text-[12.5px] leading-relaxed text-[var(--sd-muted-fg)]"
                    data-testid="settings-cancel-guidance"
                  >
                    {user?.subscription?.payment_provider === 'apple_iap' ? (
                      <>
                        Cancel anytime from your device: <strong>Settings &rarr; your Apple ID &rarr; Subscriptions &rarr; Selah Downloads</strong>. Access continues through the end of the current billing period.
                      </>
                    ) : (
                      <>
                        Manage or cancel anytime from your subscription portal. Access continues through the end of the current billing period.
                      </>
                    )}
                  </p>

                  {/* Row 4: Primary action */}
                  <Button
                    variant="outline"
                    onClick={async () => {
                      // iOS App Review 3.1.1 — external billing portals are
                      // not permitted from inside an iOS build. Deep-link
                      // users into Apple's native Subscriptions surface
                      // instead. Non-iOS builds continue to use the Stripe
                      // customer portal.
                      const target = manageSubscriptionTarget();
                      if (target.kind === 'ios-native') {
                        window.location.href = target.url;
                        return;
                      }
                      try {
                        const { url } = await api.createStripePortalSession(window.location.origin);
                        if (url) window.location.href = url;
                      } catch (e) {
                        toast.error('Could not open billing portal', { description: e?.message });
                      }
                    }}
                    className="border-[var(--sd-primary)]/50 text-[var(--sd-primary)] hover:bg-[var(--sd-primary)]/10"
                    data-testid="settings-manage-subscription-button"
                  >
                    {manageSubscriptionTarget().label}
                  </Button>
                </div>
                  );
                })()
              ) : (
                <div className="space-y-4">
                  <Badge className="bg-[var(--sd-muted-bg)] text-[var(--sd-muted-fg)] border-[var(--sd-border)]">
                    No active subscription
                  </Badge>
                  <p className="text-sm text-[var(--sd-muted-fg)] leading-relaxed">
                    Subscribe to unlock ambient capture, deeper analysis, follow-up timelines, and every future feature.
                  </p>
                  <Button
                    onClick={startSubscribe}
                    className="bg-[var(--sd-primary)] hover:brightness-110 text-[var(--sd-primary-fg)] font-semibold"
                    data-testid="settings-subscribe-button"
                  >
                    <CreditCard size={16} className="mr-2" />
                    Subscribe — {`$${getDisplayPriceUSD().toFixed(2)} / mo`}
                  </Button>
                </div>
              )}

              {/* Restore Purchases \u2014 required by Apple for IAP-enabled apps */}
              <div className="mt-6 pt-6 border-t border-[var(--sd-border)]">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-[var(--sd-fg)] mb-1">
                      Restore Purchases
                    </h3>
                    <p className="text-xs text-[var(--sd-muted-fg)] leading-relaxed max-w-md">
                      Reinstalled the app or switched devices? Restore an active subscription
                      you previously purchased.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      // Platform-aware restore.
                      //   - iOS  : Apple StoreKit 2 restore via native bridge
                      //            (App Review 3.1.1 compliant — no mention
                      //            of external processors, no Stripe call).
                      //   - Other: Stripe restore (unchanged web behaviour).
                      if (shouldUseAppleIAP()) {
                        if (!iap.isNativeIAPAvailable()) {
                          toast.error('Restore is only available in the latest app version.');
                          return;
                        }
                        try {
                          const result = await iap.restore();
                          if (
                            !result.ok ||
                            !Array.isArray(result.signedTransactions) ||
                            result.signedTransactions.length === 0
                          ) {
                            toast('No previous purchases found', {
                              description:
                                'If you subscribed before, sign in with that same Apple ID.',
                            });
                            return;
                          }
                          const verify = await api.restoreAppleTransactions({
                            signed_transactions: result.signedTransactions,
                          });
                          if (verify?.is_active) {
                            toast.success('Subscription restored', {
                              description: 'Full access is now unlocked.',
                            });
                            setTimeout(() => window.location.reload(), 900);
                          } else {
                            toast('No active subscription to restore', {
                              description:
                                'You can subscribe below, or contact support if this seems wrong.',
                            });
                          }
                        } catch (e) {
                          toast.error('Could not restore purchases', { description: e?.message });
                        }
                        return;
                      }

                      // Non-iOS: Stripe restore (unchanged).
                      try {
                        const res = await api.restoreStripePurchases();
                        if (res?.restored) {
                          toast.success('Subscription restored', {
                            description: 'Full access is now unlocked.',
                          });
                          setTimeout(() => window.location.reload(), 900);
                        } else {
                          toast.info(res?.message || 'No active subscription found on this account.');
                        }
                      } catch (e) {
                        toast.error('Could not restore purchases', { description: e?.message });
                      }
                    }}
                    className="border-[var(--sd-primary)]/50 text-[var(--sd-primary)] hover:bg-[var(--sd-primary)]/10 whitespace-nowrap"
                    data-testid="restore-purchases-button"
                  >
                    Restore
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Privacy & AI (Apple 5.1.1(i) compliance) */}
          <PrivacyAICard />

          {/* Danger Zone */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="glass-card p-6 border-[#DC6F6F]/40" data-testid="settings-danger-zone-card">
              <div className="flex items-center gap-3 mb-6">
                <Trash2 size={24} className="text-[#DC6F6F]" />
                <h2 className="text-xl font-semibold text-[var(--sd-fg)]">Danger Zone</h2>
              </div>

              <p className="text-sm text-[var(--sd-muted-fg)] leading-relaxed mb-4">
                Once you delete your account, there is no going back. All your downloads, insights, and prayers will be permanently deleted.
              </p>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    className="bg-[#DC6F6F] hover:bg-[#E08A8A] text-[#1C1C1C]"
                    data-testid="settings-delete-account-trigger"
                  >
                    <Trash2 size={16} className="mr-2" />
                    Delete Account
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="glass-card">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-[var(--sd-fg)]">Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription className="text-[var(--sd-muted-fg)]">
                      This action cannot be undone. This will permanently delete your account and remove all your data from our servers.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="border-[var(--sd-border)] text-[var(--sd-fg)]">
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteAccount}
                      className="bg-[#DC6F6F] hover:bg-[#E08A8A] text-[#1C1C1C]"
                      data-testid="settings-delete-account-confirm"
                    >
                      Yes, delete my account
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
